import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useSnackbar } from "notistack";
import { Box, Chip, Link, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "@accredo/fe-shared/components/tables/hooks/useMultiTableData.jsx";
import { useDebounce } from "@accredo/fe-shared/hooks/useDebounce.js";

import SearchField from "./SearchField.jsx";
import ObjectCardList from "./ObjectCardList.jsx";

/**
 * The portal's flat Objekte list, one row per Objekt record - the same
 * building under two Verträge is two rows, nothing expands.
 * Built on the same DataTable/useMultiTableData as PortalContractsTable, but
 * an Objekt row isn't itself something to open - only its Vertrag is, so
 * there is no onRowClick here, just a link in the Vertrag column.
 */
export default function PortalObjectsTable() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const is_mobile = useMediaQuery(theme.breakpoints.down("md"));

  const activeSearch = searchParams.get("search") || "";

  const tableKey = "objects";

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Objekte",
        route: "/data/objekts",
        pageSize: 10,
        storageKey: "kp_table_objects",
        extraParams: {
          range: "all",
          include: ["vertrag", "display_sparten"],
          sort: [{ field: "bezeichnung", order: "asc" }],
          filters: {
            ...(activeSearch && { _searchword: activeSearch }),
          },
        },
      },
    ],
    [activeSearch]
  );

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearch]);

  // Typing stays instant (local state); the URL - and the fetch its change
  // triggers above - only updates once typing pauses.
  const [searchInput, setSearchInput] = useState(activeSearch);
  const debouncedSearchInput = useDebounce(searchInput, 400);
  const isFirstSearchRun = useRef(true);
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearchInput) {
          next.set("search", debouncedSearchInput);
        } else {
          next.delete("search");
        }
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  const handleChangePage = (nextOrUpdater) => {
    const base = {
      pageIndex: tableState.pageIndex ?? 0,
      pageSize: tableState.pageSize ?? 10,
    };
    const next =
      typeof nextOrUpdater === "function" ? nextOrUpdater(base) : nextOrUpdater;
    if (!next) {
      return;
    }
    updateTableState(tableKey, {
      pageIndex: next.pageIndex,
      pageSize: next.pageSize,
    });
  };

  const handleVertragClick = (vertrag) => navigate(`/vertraege/${vertrag.id}`);

  const columns = [
    {
      header: "Objekt",
      accessorKey: "bezeichnung",
      size: 320,
      Cell: ({ row }) => {
        const objekt = row.original;
        const address = [
          [objekt.strasse, objekt.hausnr].filter(Boolean).join(" "),
          [objekt.plz, objekt.ort].filter(Boolean).join(" "),
        ]
          .filter(Boolean)
          .join(", ");

        return (
          <Typography variant="body2" fontWeight={600}>
            {objekt.bezeichnung || address}
          </Typography>
        );
      },
    },
    {
      id: "sparte",
      header: "Sparte",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(row.original.sparten || []).map((sparte) => (
            <Chip key={sparte.id} size="small" label={sparte.sparte} />
          ))}
        </Stack>
      ),
    },
    {
      id: "vertrag",
      header: "Vertrag",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const vertrag = row.original.vertrag;
        if (!vertrag) {
          return null;
        }

        return (
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/vertraege/${vertrag.id}`)}
          >
            {vertrag.vertragsnummer}
          </Link>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Chip
          size="small"
          label={row.original.is_active ? "aktiv" : "archiviert"}
          variant={
            row.original.is_active
              ? "contract-status_aktiv"
              : "contract-status_archiviert"
          }
        />
      ),
    },
  ];

  return (
    <Box>
      <SearchField
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
        isLoading={tableState.isLoading}
      />
      {is_mobile ? (
        <ObjectCardList
          rows={rows}
          isLoading={tableState.isLoading}
          hasLoaded={tableState.hasLoaded}
          pageIndex={tableState.pageIndex}
          pageSize={tableState.pageSize}
          totalRows={tableState.totalRows}
          onChangePage={handleChangePage}
          onVertragClick={handleVertragClick}
        />
      ) : (
        <DataTable
          testId="portal-objects-table"
          columns={columns}
          data={rows}
          isLoading={tableState.isLoading}
          hasLoaded={tableState.hasLoaded}
          pageIndex={tableState.pageIndex}
          pageSize={tableState.pageSize}
          totalRows={tableState.totalRows}
          onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
          onChangePage={handleChangePage}
          storageKey="kp_table_objects"
          enableSorting
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableColumnHiding={false}
          enableTopToolbar={false}
        />
      )}
    </Box>
  );
}
